import type {
  AuthSessionProjection,
  LeanAuthHttpAdapter,
  PublicRuntimeConfig,
} from '../../server/auth/contracts.js';

export type AuthBootstrapState =
  | { kind: 'loading' }
  | { kind: 'disabled'; config: PublicRuntimeConfig }
  | { kind: 'signed_out'; config: PublicRuntimeConfig }
  | { kind: 'provider_degraded'; config: PublicRuntimeConfig }
  | {
      kind: 'pending_profile';
      config: PublicRuntimeConfig;
      session: Extract<AuthSessionProjection, { accountStatus: 'pending_profile' }>;
    }
  | {
      kind: 'active';
      config: PublicRuntimeConfig;
      session: Extract<AuthSessionProjection, { accountStatus: 'active' }>;
    }
  | { kind: 'error'; message: string };

export interface LeanAuthController {
  getState(): AuthBootstrapState;
  subscribe(listener: () => void): () => void;
  bootstrap(): Promise<void>;
  retry(): Promise<void>;
  saveProfile(displayName: string): Promise<void>;
  logoutCurrent(): Promise<void>;
}

function safeError(reason: unknown, fallback: string): string {
  if (!(reason instanceof Error) || reason.message.length === 0) return fallback;
  return reason.message.slice(0, 240);
}

function resolvedState(
  config: PublicRuntimeConfig,
  session: AuthSessionProjection | null,
): AuthBootstrapState {
  if (config.authPolicy === 'disabled') return { kind: 'disabled', config };
  if (session?.accountStatus === 'pending_profile') {
    return { kind: 'pending_profile', config, session };
  }
  if (session?.accountStatus === 'active') return { kind: 'active', config, session };
  if (config.loginIssuance === 'degraded') return { kind: 'provider_degraded', config };
  return { kind: 'signed_out', config };
}

export function authAllowsAnonymousOrActiveApplication(state: AuthBootstrapState): boolean {
  return state.kind === 'disabled'
    || state.kind === 'active'
    || ((state.kind === 'signed_out' || state.kind === 'provider_degraded')
      && state.config.authPolicy === 'optional');
}

export function activeDisplayName(state: AuthBootstrapState): string | undefined {
  return state.kind === 'active' ? state.session.profile.displayName : undefined;
}

export function createLeanAuthController(adapter: LeanAuthHttpAdapter): LeanAuthController {
  let state: AuthBootstrapState = { kind: 'loading' };
  let bootstrapPromise: Promise<void> | undefined;
  let revision = 0;
  const listeners = new Set<() => void>();

  function publish(next: AuthBootstrapState): void {
    state = next;
    for (const listener of listeners) listener();
  }

  async function load(force: boolean): Promise<void> {
    if (!force && bootstrapPromise !== undefined) return bootstrapPromise;
    const ownRevision = ++revision;
    publish({ kind: 'loading' });
    const pending = (async () => {
      try {
        const config = await adapter.getRuntimeConfig();
        if (config.authPolicy === 'disabled') {
          if (revision === ownRevision) publish({ kind: 'disabled', config });
          return;
        }
        const session = await adapter.getSession();
        if (revision === ownRevision) publish(resolvedState(config, session));
      } catch (reason) {
        if (revision === ownRevision) {
          publish({ kind: 'error', message: safeError(reason, 'Не удалось проверить вход.') });
        }
      }
    })();
    bootstrapPromise = pending;
    await pending;
  }

  return {
    getState: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    bootstrap: () => load(false),
    retry: () => load(true),
    async saveProfile(displayName: string): Promise<void> {
      const current = state;
      if (current.kind !== 'pending_profile' && current.kind !== 'active') {
        throw new Error('Профиль нельзя изменить без активной account session.');
      }
      const session = await adapter.updateProfile(
        { displayName },
        current.session.csrfToken,
      );
      if (state !== current) throw new Error('Account session изменилась во время сохранения.');
      publish({ kind: 'active', config: current.config, session });
    },
    async logoutCurrent(): Promise<void> {
      const current = state;
      if (current.kind !== 'pending_profile' && current.kind !== 'active') return;
      await adapter.logoutCurrent(current.session.csrfToken);
      // A committed logout clears the cookie and wins over any concurrent
      // profile response from the same session.
      publish(resolvedState(current.config, null));
    },
  };
}
